import React from 'react'
import { isString } from 'lodash'
import {  ButtonGroup, Table, Glyphicon } from 'react-bootstrap'

import Button from '../../components/Button.jsx'
import TripStatus from '../components/TripStatusSelect.jsx'
import { printMoney } from '../../../lib/Tools.js'
import { getText } from '../../lib/text.js'
import Circle from '../../components/Circle'
import TagsList from '../../tags/TagsList'

import { arrangeTripsList, processTripsList } from './arrange'

/**
 * per-tab settings
 */

const tabsWithUrgency = new Set(["nuevos", "propuesta", "negociando", "contrato"])
const tabsWithDate = new Set(["nuevos", "propuesta", "negociando", "contrato", "aprobados", "cerrados", "perdidos", "otros"])
const tabsWithBill = new Set(["cerrados"])

/**
 * 
 */

type TripsTableProps = {
  trips: Object[],
  tab: number,
  updateStatus: Function,
  updateComment: Function, 
  updateTags: Function, 
  onEdit: Function,
  onDelete: Function,
  openModal: Function,
  onSort: string => void,
  sortColumn: string, 
  sortDirection: string, 
  getPendingTasksCount: string => number
}

const TripsTable = (
  { 
    trips, Routes, Activities, Cities, 
    username, activeUserNames, query, onEdit, onDelete, 
    tab, updateStatus, updateComment, updateTags, openModal, 
    onSort, sortColumn, sortDirection, getPendingTasksCount, tags
  }: TripsTableProps
) => {
  const tripsData = processTripsList(trips, Routes, Activities, Cities, getPendingTasksCount)

  // there is no urgency in the later tabs; spot clients have no arrival  
  const includeUrgency = tabsWithUrgency.has(tab)
  const includeDate = tabsWithDate.has(tab)
  const includeBill = tabsWithBill.has(tab)

  let _sortColumn = sortColumn
  if ((_sortColumn == "arrival" || _sortColumn == "bill") && includeUrgency) _sortColumn = "urgency" 
  if ((_sortColumn == "urgency" || _sortColumn == "bill") && (!includeUrgency && !includeBill)) _sortColumn = "arrival" 
  if ((_sortColumn == "urgency" || _sortColumn == "arrival") && includeBill) _sortColumn = "bill" 
      
  const sortedTripsData = arrangeTripsList(tripsData, _sortColumn, sortDirection)

  const columnHeaders = [ 
    { text: getText("Estado"),      sortable: true,   sortKey: "status" },
    ...( includeUrgency ? [{ text: getText("Urgente"), sortable: true, sortKey: "urgency" }] : []), 
    /*{ text: getText("Clase"),      sortable: true,    sortKey: "stars" }, */
    { text: getText("Total"),       sortable: true,   sortKey: "price" },
    ...( includeBill ? [{ text: getText("Factura"),   sortable: true, sortKey: "bill" }] : []), 
    { text: getText("Nombre"),      sortable: false,  sortKey: "" },
    { text: getText("Grupo"),       sortable: false,  sortKey: "" },
    { text: getText("Encargado"),   sortable: false,  sortKey: "" },
    ...(includeDate ? [{ text: getText("Fechas"),     sortable: true, sortKey: "arrival" }] : []),
    { text: getText("Creación"),    sortable: true,   sortKey: "creation" },
    { text: getText(""),  sortable: false,  sortKey: "" },
  ] 

  return (
    <div>
      <Table bordered striped>
        <thead
          style={{ height: 35, position: "sticky", top: 40, background: "white", zIndex: 20 }}
        >
          <tr>
            <th />
            {
              columnHeaders.map(({ text, sortable, sortKey }) => (
                  sortable 
                  ? (
                    <th
                      onClick={() => onSort(sortKey)}
                      style={{ textDecoration: _sortColumn === sortKey ? "underline" : "none", cursor: "pointer" }}
                    >
                      <Glyphicon glyph="sort" />{text}
                    </th>
                  )
                  : (<th>{text}</th>)
                ))
            }
          </tr>
        </thead>
        <tbody>
          {
            sortedTripsData.map(tripValues => (
              isString(tripValues) 
                ? (<tr><td colSpan={columnHeaders.length + 1}>{tripValues}</td></tr>)
                : (
                  <tr key={tripValues._id}>
                    <td style={{ width: 95 }}>
                      <ButtonGroup>
                        <Button 
                          bsSize="xsmall"
                          style={{color: "red"}}
                          glyph="remove"
                          onClick={() => onDelete(tripValues._id)}
                        />
                        <Button 
                          bsSize="xsmall"
                          glyph="pencil"
                          onClick={() => onEdit(tripValues._id)}
                        >
                          {getText("editButton")}
                        </Button>
                      </ButtonGroup>
                    </td>
                    <td style={{ width: 120, padding: 0, margin: 0 }}>
                      <TripStatus 
                        status={tripValues.status} 
                        onChange={x => updateStatus(tripValues._id, x)} 
                        includeLabel={false} 
                        extraThin
                        shorter
                      />
                    </td>
                    { 
                      includeUrgency && (
                        <td style={{ width: 40 }}>
                          { /* new stuff */ }
                          <Circle 
                            percentage={tripValues.elapsedP}
                            text={`${tripValues.remainingTime}d`}
                            size="xsmall"
                            color={(tripValues.elapsedP <= 10 && "green") || (tripValues.elapsedP <= 20 && "orange") || "red" }
                          />
                          { /* {trip.price.finalE} {trip.remainingP} {trip.totalTime} {trip.remainingTime} */ }
                        </td>
                      )
                    }
                    <td style={{ width: 40 }}>{printMoney(tripValues.paid)} / {printMoney(tripValues.price.finalE)}€</td>
                    {
                      includeBill && (
                        <td 
                          style={{ width: 40 }}
                        > 
                          {tripValues.receiptNumber ? 
                            <div><div style={{marginBottom: 5}}> {tripValues.receiptNumber}/{tripValues.receiptYear}</div>
                              <div style={{fontSize: 12}}> {tripValues.receiptDate}</div></div> 
                          : "" 
                          }
                        </td>)
                    }
                    <td>
                      <span style={{ width: 150, fontWeight: "bold", fontSize: 16 }}>{tripValues.name}</span>
                      <span style={{ fontSize: 12 }}>{ tripValues.realName ? `  (${tripValues.realName})` : "" }</span>
                    </td>
                    <td style={{ width: 50 }}>{tripValues.people}</td>
                    { 
                      ((tripValues.encargado 
                        && (
                          activeUserNames.has(tripValues.encargado) || tab == "cerrados" || tab == "perdidos"
                            ? <td style={{ width: 80 }}>{tripValues.encargado}</td> 
                            : <td style={{ width: 80, fontWeight: "bold" }}>{`⚠️ ${getText("En️cargado desactivado")} (${tripValues.encargado})`}</td>
                        ))
                        || <td style={{ width: 80, fontWeight: "bold" }}>{`⚠️ ${getText("Sin encargado️")}`}</td>
                      )
                    }
                    { 
                      includeDate && (
                        <td style={{ width: 120 }}>{tripValues.dates}</td>)
                    }
                    <td style={{ width: 60 }}>{tripValues.creation}</td>

                    <td 
                      style={{ width: 205,  }}
                    >
                      <div 
                        style={{ 
                          position: "relative", width: "100%", height: "100%", lineHeight: "1", 
                          fontSize: tripValues.lastComment.length > 120 ? 12 : 14,
                          marginBottom: tripValues.lastComment ? 6 : 0 
                        }}
                      >
                        {tripValues.lastComment}   
                      </div>
                      <div style={{display:"inline-block"}}>
                        <TagsList 
                          tagType="trips"
                          selectedTags={tripValues.tags} 
                          allTags={tags} 
                          onChange={newSelection => updateTags(tripValues._id, newSelection)}
                        />
                      </div>
                    </td>
                  </tr>
                )
            ))
          }
        </tbody>
      </Table>
    </div>
  )
}

export default TripsTable
